<script lang="ts" setup>
  import { ECashierTab } from '~/store/cashier';
  import { useUiStore } from '~/store/ui';

  const emit = defineEmits<{ close: [] }>();
  const { content, handleItemClick, openCashier } = useSidebarLinks(emit);

  const { store: tournamentStore } = useTournamentsLoader();
  const tournamentsFeatureEnabled = computed(() => !!tournamentStore.value?.isEnabled);

  const { store: vipStore } = useVipFeature();
  const vipFeatureEnabled = computed(() => !!vipStore.value?.isEnabled);

  const { store: welcomeStore } = useWelcomeFeature();
  const welcomeFeatureAvailable = computed(() => !!welcomeStore.value?.isAvailable);

  const uiStore = useUiStore();
</script>

<template>
  <aside
    :class="{
      'w-[300px] min-[1792px]:w-[352px]': uiStore.sidebarIsOpen,
      'w-[110px]': !uiStore.sidebarIsOpen,
    }"
    class="contain-inline-size sidebar-container @container relative ml-5 top-[98px] z-10 shrink-0 ease-in-out transition-[width] duration-300"
  >
    <div
      :class="{
        'w-[300px] min-[1792px]:w-[352px]': uiStore.sidebarIsOpen,
        'w-[110px]': !uiStore.sidebarIsOpen,
      }"
      class="fixed left-5 top-[98px] h-[calc(100vh-124px)] transition-width duration-300 rounded-md bg-white/5 backdrop-blur-[25px] px-5 py-4 @max-[120px]:px-2.5 overflow-auto"
    >
      <LazyLeaderboardSidebarBanner v-if="tournamentsFeatureEnabled" :is-compact="!uiStore.sidebarIsOpen" />
      <LazyVipSidebarSection v-if="vipFeatureEnabled && !welcomeFeatureAvailable" />
      <LazyWelcomeSidebarWidget v-if="welcomeFeatureAvailable" :is-compact="!uiStore.sidebarIsOpen" />

      <AppUserBalance v-if="uiStore.sidebarIsOpen" sidebar />

      <Transition name="fade-delayed">
        <div
          v-show="uiStore.sidebarIsOpen"
          :class="{
            'opacity-0': !uiStore.sidebarIsOpen,
          }"
          class="@max-[120px]:hidden my-2.5 grid grid-cols-2 gap-1.5 h-[52px]"
        >
          <CoreButton
            class="justify-center cursor-pointer"
            color="secondary"
            size="xl"
            @click="openCashier(ECashierTab.Withdraw)"
          >
            <span class="font-inter text-base font-bold leading-none">Withdraw</span>
          </CoreButton>
          <CoreButton
            class="justify-center cursor-pointer"
            color="primary"
            size="xl"
            @click="openCashier(ECashierTab.Deposit)"
          >
            <span class="font-inter text-base font-bold leading-none">Deposit</span>
          </CoreButton>
        </div>
      </Transition>

      <div class="@[120px]:hidden flex flex-col gap-2.5 mb-5">
        <CoreButton
          class="justify-center cursor-pointer h-[52px]"
          color="secondary"
          size="xl"
          @click="openCashier(ECashierTab.Withdraw)"
        >
          <span class="font-inter text-[15px] font-semibold leading-normal">Withdraw</span>
        </CoreButton>
        <CoreButton
          class="justify-center cursor-pointer h-[52px]"
          color="primary"
          size="xl"
          @click="openCashier(ECashierTab.Deposit)"
        >
          <span class="font-inter text-[15px] font-semibold leading-normal">Deposit</span>
        </CoreButton>
      </div>

      <div class="w-full h-[1px] bg-white/10 my-2.5" />

      <div class="mt-6 font-barlow">
        <div
          v-for="item in content"
          :key="item.key"
          :class="{ 'bg-white/7': item.isActive }"
          class="rounded-[10px] overflow-hidden select-none cursor-pointer hover:bg-white/1 transition-bg ease-in-out duration-200"
          @click="handleItemClick(item)"
        >
          <div v-if="item.isDivider" class="w-full h-[1px] bg-white/10 my-2.5" />
          <div v-else class="relative flex items-center px-1 py-2">
            <div v-if="item.isActive" class="h-full w-1 absolute left-0 bg-blue" />
            <div
              :class="{
                'ml-[11px]': !uiStore.sidebarIsOpen,
                'mr-[11px]': !uiStore.sidebarIsOpen,
              }"
              class="flex items-center justify-center transition-[margin] ease-in-out duration-200 w-[60px] h-10 flex-shrink-0"
            >
              <component
                :is="`Svgo${item.icon}`"
                :class="{
                  'text-blue': item.isActive,
                  'text-white': !item.isActive,
                }"
                class="size-5"
              />
            </div>
            <Transition name="slide-fade">
              <span
                v-if="uiStore.sidebarIsOpen && item.title"
                class="mr-1.5 text-white text-nowrap text-base select-none font-medium leading-normal transition-opacity duration-150 flex-shrink-1"
              >
                {{ item.title }}
              </span>
            </Transition>
            <div
              v-if="uiStore.sidebarIsOpen && item.badge && item.badge.value"
              :class="{
                'rounded-full pl-1 px-1.5': item.badge.variant === 'round',
                'px-2 py-1 rounded-sm': item.badge.variant === 'square',
              }"
              class="bg-[#dc0000]"
            >
              <div
                :class="{
                  'text-white text-xs': item.badge.variant === 'round',
                  'text-[#ECECEC] text-[10px]': item.badge.variant === 'square',
                }"
                class="font-inter font-black text-nowrap leading-normal text-center"
              >
                {{ item.badge.value }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>
